import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function AnnouncementsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleCreateAnnouncement = async () => {
    try {
      if (editingAnnouncement?.id) {
        await axios.put(`/announcements/${editingAnnouncement.id}`, {
          title: editingAnnouncement.title,
          content: editingAnnouncement.content,
          type: editingAnnouncement.type,
          active: editingAnnouncement.active
        });
        toast.success('Announcement updated');
      } else {
        await axios.post('/announcements', {
          title: editingAnnouncement.title,
          content: editingAnnouncement.content,
          type: editingAnnouncement.type,
          active: editingAnnouncement.active
        });
        toast.success('Announcement created');
      }
      setShowAnnouncementDialog(false);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleToggleAnnouncement = async (id) => {
    try {
      await axios.patch(`/announcements/${id}/toggle`);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to toggle announcement');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  return (
    <div data-testid="announcements-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Announcements</h1>
        <Button onClick={() => { setEditingAnnouncement({ title: '', content: '', type: 'info', active: true }); setShowAnnouncementDialog(true); }}>
          <Megaphone className="w-4 h-4 mr-2" />New Announcement
        </Button>
      </div>
      
      {loading ? (
        <p className="text-center text-slate-500 py-8">Loading announcements...</p>
      ) : announcements.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No announcements yet</p>
            <Button onClick={() => { setEditingAnnouncement({ title: '', content: '', type: 'info', active: true }); setShowAnnouncementDialog(true); }} variant="outline">
              Create First Announcement
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <Card key={announcement.id} className={`border-l-4 ${
              announcement.type === 'warning' ? 'border-l-amber-500 bg-amber-50' :
              announcement.type === 'error' ? 'border-l-red-500 bg-red-50' :
              'border-l-blue-500 bg-blue-50'
            }`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Megaphone className={`w-5 h-5 ${
                        announcement.type === 'warning' ? 'text-amber-600' :
                        announcement.type === 'error' ? 'text-red-600' :
                        'text-blue-600'
                      }`} />
                      <p className="font-semibold text-lg">{announcement.title}</p>
                      {announcement.active && <Badge className="bg-green-100 text-green-700">Active</Badge>}
                      {!announcement.active && <Badge className="bg-slate-100 text-slate-500">Inactive</Badge>}
                    </div>
                    <p className="text-slate-600 mb-2">{announcement.content}</p>
                    <p className="text-xs text-slate-400">
                      Posted: {announcement.created_at ? new Date(announcement.created_at).toLocaleDateString() : 'N/A'}
                      {announcement.created_by_name && ` by ${announcement.created_by_name}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingAnnouncement(announcement); setShowAnnouncementDialog(true); }}>
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleToggleAnnouncement(announcement.id)}
                    >
                      {announcement.active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteAnnouncement(announcement.id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {showAnnouncementDialog && (
        <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingAnnouncement?.id ? 'Edit Announcement' : 'New Announcement'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title</Label>
                <Input 
                  value={editingAnnouncement?.title || ''} 
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, title: e.target.value})}
                  placeholder="Announcement title"
                />
              </div>
              <div>
                <Label>Type</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={editingAnnouncement?.type || 'info'}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, type: e.target.value})}
                >
                  <option value="info">Information</option>
                  <option value="warning">Warning</option>
                  <option value="error">Critical</option>
                </select>
              </div>
              <div>
                <Label>Content</Label>
                <textarea 
                  className="w-full border rounded-md p-2 min-h-[100px]"
                  value={editingAnnouncement?.content || ''}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, content: e.target.value})}
                  placeholder="Announcement content..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={editingAnnouncement?.active || false}
                  onChange={(e) => setEditingAnnouncement({...editingAnnouncement, active: e.target.checked})}
                />
                <Label>Active (visible to all users)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateAnnouncement}>Save Announcement</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
